import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, LogIn } from "lucide-react";
import { toast } from "sonner";

const NIVELES = [1, 2, 3, 4];

export default function Tenants() {
  const { selectScopeTenant, scopeTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", niveles_activos: [1], admin_email: "", admin_password: "", admin_nombre: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/tenants");
    setTenants(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleNivel = (n, setter, current) =>
    setter(current.includes(n) ? current.filter((x) => x !== n) : [...current, n].sort());

  const crear = async () => {
    if (!form.nombre || !form.admin_email || !form.admin_password) return toast.error("Complete los campos obligatorios");
    try {
      await api.post("/tenants", form);
      toast.success("Tenant creado");
      setOpen(false);
      setForm({ nombre: "", niveles_activos: [1], admin_email: "", admin_password: "", admin_nombre: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const cambiarNiveles = async (t, niveles) => {
    try {
      await api.patch(`/tenants/${t.id}/niveles`, { niveles_activos: niveles });
      toast.success("Niveles actualizados");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const entrar = (t) => {
    selectScopeTenant(t.id, t.nombre);
    toast.success(`Operando sobre ${t.nombre}`);
    navigate("/");
  };

  return (
    <div data-testid="tenants-page">
      <PageHeader overline="Superadmin · Agentik" title="Gestión de Tenants"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="nuevo-tenant-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" /> Nuevo tenant</Button>
            </DialogTrigger>
            <DialogContent className="bg-white" data-testid="tenant-dialog">
              <DialogHeader><DialogTitle className="text-[#0E2A47]">Crear tenant</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Nombre de la empresa</Label>
                  <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="tenant-nombre" className="mt-1.5" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Niveles activos</Label>
                  <div className="flex gap-4 mt-2">
                    {NIVELES.map((n) => (
                      <label key={n} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={form.niveles_activos.includes(n)}
                          onCheckedChange={() => toggleNivel(n, (v) => setForm({ ...form, niveles_activos: v }), form.niveles_activos)}
                          data-testid={`tenant-nivel-${n}`} /> Nivel {n}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Admin nombre</Label>
                    <Input value={form.admin_nombre} onChange={(e) => setForm({ ...form, admin_nombre: e.target.value })} data-testid="tenant-admin-nombre" className="mt-1.5" /></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Admin email</Label>
                    <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="tenant-admin-email" className="mt-1.5" /></div>
                </div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Admin contraseña</Label>
                  <Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="tenant-admin-password" className="mt-1.5" /></div>
              </div>
              <DialogFooter><Button onClick={crear} data-testid="tenant-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Crear</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <Card className="border-[#E2E8F0] overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-[#F7F8FA]">
            <TableHead>Empresa</TableHead><TableHead>Niveles activos</TableHead><TableHead>Usuarios</TableHead>
            <TableHead>Docs</TableHead><TableHead>API Key</TableHead><TableHead className="text-right">Acción</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tenants.map((t) => (
              <TableRow key={t.id} data-testid={`tenant-row-${t.id}`} className={`hover:bg-[#F7F8FA] ${scopeTenant === t.id ? "bg-[#FFF3EF]" : ""}`}>
                <TableCell className="font-medium text-[#0E2A47]">{t.nombre}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {NIVELES.map((n) => (
                      <button key={n} onClick={() => cambiarNiveles(t, toggleArr(t.niveles_activos, n))}
                        data-testid={`tenant-toggle-nivel-${t.id}-${n}`}
                        className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                          t.niveles_activos.includes(n) ? "bg-[#0E2A47] text-white" : "bg-[#E2E8F0] text-[#0E2A47]/40 hover:bg-[#CBD5E1]"}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </TableCell>
                <TableCell>{t.usuarios}</TableCell>
                <TableCell>{t.documentos}</TableCell>
                <TableCell className="font-mono text-[10px] text-[#0E2A47]/50">{t.api_key}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" onClick={() => entrar(t)} data-testid={`tenant-entrar-${t.id}`} className="bg-[#FF5625] hover:bg-[#E0451A]">
                    <LogIn size={14} className="mr-1" /> Entrar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function toggleArr(arr, n) {
  return arr.includes(n) ? arr.filter((x) => x !== n) : [...arr, n].sort();
}
